import { type AuthOptions, PASSWORD_MIN, USERNAME_RULES } from '@chessbut/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { NATIVE } from '../lib/platform.ts';
import { authOptions, changeUsername, login, signup, startEmail, startOAuth, useSession, verifyEmailCode } from '../lib/session.ts';
import { track } from '../lib/track.ts';
import { Modal } from './Modal.tsx';
import { toast } from './Toast.tsx';

type View = 'email' | 'code' | 'password' | 'welcome';

/**
 * Accounts are optional: everything already saves in this browser. The default way in is an
 * emailed code (no password); username + password and Google / Apple are alternatives.
 */
export function AuthModal({ initial = 'signup', reason, onClose }: { initial?: 'signup' | 'login'; reason?: string; onClose(): void }) {
  const [opts, setOpts] = useState<AuthOptions | null>(null);
  const [view, setView] = useState<View>('email');
  const [pwMode, setPwMode] = useState<'signup' | 'login'>(initial);
  const [email, setEmail] = useState('');
  const [request, setRequest] = useState<{ id: string; sentTo: string; at: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void authOptions().then((o) => {
      setOpts(o);
      if (!o.email) setView('password');
    });
  }, []);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (e?: FormEvent) => {
    e?.preventDefault();
    return run(async () => {
      const r = await startEmail(email.trim());
      setRequest({ ...r, at: Date.now() });
      setView('code');
      track('auth.code');
    });
  };

  const title = view === 'welcome' ? 'You’re in' : view === 'password' && pwMode === 'login' ? 'Log in' : 'Save your progress';

  return (
    <Modal title={title} onClose={onClose}>
      {!opts ? (
        <div className="center">
          <span className="spinner" />
        </div>
      ) : view === 'email' ? (
        <div className="auth">
          <p className="auth-lede">{reason ?? 'Pick your own name and play on any device. No password needed.'}</p>
          <form onSubmit={sendCode} className="auth-form">
            <label htmlFor="auth-email">Email</label>
            <input id="auth-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            {error && <p className="form-error">{error}</p>}
            <button className="btn primary big block" disabled={busy || !email.includes('@')}>
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
            <p className="hint-text center">New here or coming back, it’s the same button. We only use your email to sign you in.</p>
          </form>
          <Alternatives opts={opts} onPassword={() => { setError(null); setView('password'); }} />
        </div>
      ) : view === 'code' && request ? (
        <CodeStep
          request={request}
          busy={busy}
          error={error}
          onVerify={(code) =>
            run(async () => {
              const r = await verifyEmailCode(request.id, code);
              if (r.isNew) setView('welcome');
              else {
                toast(`Welcome back, ${r.me.username}!`);
                onClose();
              }
            })
          }
          onResend={() => sendCode()}
          onBack={() => {
            setError(null);
            setView('email');
          }}
        />
      ) : view === 'welcome' ? (
        <Welcome onDone={onClose} />
      ) : (
        <PasswordStep
          mode={pwMode}
          setMode={setPwMode}
          busy={busy}
          error={error}
          canEmail={opts.email}
          onBack={() => {
            setError(null);
            setView('email');
          }}
          onSubmit={(username, password) =>
            run(async () => {
              if (pwMode === 'signup') {
                const m = await signup(username, password);
                toast(`Welcome to ChessBut, ${m.username}!`);
              } else {
                const m = await login(username, password);
                toast(`Welcome back, ${m.username}!`);
              }
              onClose();
            })
          }
        />
      )}
    </Modal>
  );
}

function Alternatives({ opts, onPassword }: { opts: AuthOptions; onPassword(): void }) {
  const [busy, setBusy] = useState(false);
  const go = async (provider: 'google' | 'apple') => {
    setBusy(true);
    try {
      await startOAuth(provider);
    } catch (err) {
      toast((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="auth-alt">
      {(opts.google || opts.apple) && <div className="auth-or">or</div>}
      {opts.apple && (
        <button className="btn block" disabled={busy} onClick={() => go('apple')}>
          Continue with Apple
        </button>
      )}
      {opts.google && (
        <button className="btn block" disabled={busy} onClick={() => go('google')}>
          Continue with Google
        </button>
      )}
      <button className="text-link" onClick={onPassword}>
        Use a username and password instead
      </button>
    </div>
  );
}

function CodeStep({
  request,
  busy,
  error,
  onVerify,
  onResend,
  onBack,
}: {
  request: { sentTo: string; at: number };
  busy: boolean;
  error: string | null;
  onVerify(code: string): void;
  onResend(): void;
  onBack(): void;
}) {
  const [code, setCode] = useState('');
  const [now, setNow] = useState(Date.now());
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => setCode(''), [request.at]);
  const wait = Math.max(0, 30 - Math.floor((now - request.at) / 1000));
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (code.length === 6) onVerify(code);
  };
  return (
    <form className="auth auth-form" onSubmit={submit}>
      <p className="auth-lede">
        We sent a 6-digit code to <b>{request.sentTo}</b>. It can take a minute (check spam too).
      </p>
      <label htmlFor="auth-code">Code</label>
      <input
        id="auth-code"
        ref={input}
        className="code-input"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        value={code}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 6);
          setCode(v);
          if (v.length === 6) onVerify(v);
        }}
        placeholder="123456"
        required
      />
      {error && <p className="form-error">{error}</p>}
      <button className="btn primary big block" disabled={busy || code.length !== 6}>
        {busy ? 'Checking…' : 'Sign in'}
      </button>
      <div className="auth-row">
        <button type="button" className="text-link" onClick={onBack}>
          Different email
        </button>
        <button type="button" className="text-link" disabled={busy || wait > 0} onClick={onResend}>
          {wait > 0 ? `Resend in ${wait}s` : 'Resend code'}
        </button>
      </div>
      <p className="hint-text center">{NATIVE ? 'The link in the email opens the website. Type the code here to sign in to the app.' : 'Or tap the link in the email to sign in on that device.'}</p>
    </form>
  );
}

/** First sign-in: show the name we picked and offer to change it right there. */
function Welcome({ onDone }: { onDone(): void }) {
  const { me } = useSession();
  const [name, setName] = useState(me?.username ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() === me?.username) return onDone();
    setBusy(true);
    setError(null);
    try {
      await changeUsername(name.trim());
      toast('Name saved');
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth auth-form" onSubmit={save}>
      <p className="auth-lede">Your games, rating and streak are saved to your account. This is the name everyone sees:</p>
      <label htmlFor="auth-name">Your name</label>
      <input id="auth-name" autoCapitalize="off" spellCheck={false} maxLength={20} value={name} onChange={(e) => setName(e.target.value)} required />
      <p className="hint-text">{USERNAME_RULES}. You can change it later in Settings.</p>
      {error && <p className="form-error">{error}</p>}
      <button className="btn primary big block" disabled={busy}>
        {name.trim() === me?.username ? 'Looks good' : busy ? 'Saving…' : 'Save name'}
      </button>
    </form>
  );
}

function PasswordStep({
  mode,
  setMode,
  busy,
  error,
  canEmail,
  onBack,
  onSubmit,
}: {
  mode: 'signup' | 'login';
  setMode(m: 'signup' | 'login'): void;
  busy: boolean;
  error: string | null;
  canEmail: boolean;
  onBack(): void;
  onSubmit(username: string, password: string): void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  return (
    <div className="auth">
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={mode === 'signup'} onClick={() => setMode('signup')}>
          New account
        </button>
        <button role="tab" aria-selected={mode === 'login'} onClick={() => setMode('login')}>
          Log in
        </button>
      </div>
      <form
        className="auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(username.trim(), password);
        }}
      >
        <label htmlFor="auth-user">Username</label>
        <input id="auth-user" autoComplete="username" autoCapitalize="off" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value)} maxLength={20} required />
        {mode === 'signup' && <p className="hint-text">{USERNAME_RULES}</p>}
        <label htmlFor="auth-pass">Password</label>
        <input id="auth-pass" type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={(e) => setPassword(e.target.value)} minLength={mode === 'signup' ? PASSWORD_MIN : undefined} required />
        {error && <p className="form-error">{error}</p>}
        <button className="btn primary big block" disabled={busy}>
          {busy ? 'One sec…' : mode === 'signup' ? 'Create account' : 'Log in'}
        </button>
        <p className="hint-text center">{mode === 'signup' ? 'Everything you’ve played on this device comes with you.' : 'Anything you played here as a guest moves to your account.'}</p>
      </form>
      {canEmail && (
        <button className="text-link" onClick={onBack}>
          ← Sign in with an email code instead
        </button>
      )}
    </div>
  );
}
